import React, {
	memo,
	useCallback,
	useEffect,
	useMemo,
	useState
} from 'react';
import { useHistory } from 'react-router-dom';

import {
	handleError,
	log
} from '../../../../services/utils';
import { maskValue } from '../../../../services/masks';

import {
	getContactList,
	deleteContact,
	exportContactList
} from '../../../../api/nora/contacts';
import { downloadAsset } from '../../../../api/nora/assets';

import useDeleteConfirm from '../../../../hooks/useDeleteConfirm';

import { useAuthState } from '../../../../contexts/AuthContext';
import {
	useContactState,
	useContactAction
} from '../../../../contexts/ContactsContext';
import { useUIAction } from '../../../../contexts/UIContext';

import EmailAndPhone from '../../../Table/Cells/EmailAndPhone';
import HelpModal from '../../../Shared/HelpModal';
import Tags from '../../../Table/Cells/Tags';
import TagsColumnFilter from '../../../Table/ColumnFilters/TagsColumnFilter';
import tagsFilterFn from '../../../Table/filterFunctions/tagsFilterFn';
import MultiSelectColumnFilter from '../../../Table/ColumnFilters/MultiSelectColumnFilter';
import multiSelectFilterFn from '../../../Table/filterFunctions/multiSelectFilterFn';
import multiWordFilterFn from '../../../Table/filterFunctions/multiWordFilterFn';
import NameAndAddress from '../../../Table/Cells/NameAndAddress';
import PageHeader from '../../../Shared/PageHeader';
import Table from '../../../Table';
import TableRowActions from '../../../Table/Cells/TableRowActions';

const ContactsList = () => {
	const history = useHistory();

	const { user: { permissions } } = useAuthState();
	const {
		contactList,
		initialized,
		tableState
	} = useContactState();
	const {
		setContactList,
		setTableState
	} = useContactAction();
	const {
		setApiErrorAlert,
		setInfobar,
		setInfobarLoader,
		setScreenLoader
	} = useUIAction();

	const [ isLoading, setIsLoading ] = useState( true );

	const retrieveContactList = useCallback( async () => {
		setInfobarLoader( 'Retrieving Contact List...' );

		try {
			const data = await getContactList();

			setContactList( data );
		} catch ( error ) {
			setApiErrorAlert( error );
			throw error;
		}
	}, [
		setInfobarLoader,
		setContactList,
		setApiErrorAlert
	] );

	const handleDeleteContact = useCallback( async contact => {
		try {
			await deleteContact( contact.id );
			await retrieveContactList();
		} catch ( error ) {
			setApiErrorAlert( error );
		}
	}, [
		retrieveContactList,
		setApiErrorAlert
	] );

	const {
		DeleteConfirmAlert,
		setDeleteConfirm,
		setToDelete
	} = useDeleteConfirm( handleDeleteContact );

	useEffect( () => {
		setInfobar( <span>Operations &nbsp;&#9656;&nbsp; Contacts</span> );
	}, [ setInfobar ] );

	useEffect( () => {
		( async () => {
			if ( !initialized ) {
				setInfobarLoader( 'Please wait...' );
			} else {
				if ( permissions.listContacts.canExecute && permissions.listContacts.isAvailable ) {
					try {
						await retrieveContactList();
						setInfobarLoader( '' );
					} catch ( error ) {
						if ( error.message !== 'canceled' ) {
							handleError( error );
							setInfobarLoader( '' );
						}
					}
				} else {
					setInfobarLoader( '' ); // this only happens when the user's 'listContact permission isAvailable but not canExecute
				}

				setIsLoading( false );
			}
		} )();
	}, [
		initialized,
		permissions,
		retrieveContactList,
		setInfobarLoader
	] );

	const nameAndAddressCell = useCallback( ( { row } ) => <NameAndAddress row={ row.original } />, [] );

	const TagsCell = useCallback( ( { row } ) => <Tags row={ row.original } />, [] );

	const emailAndPhoneCell = useCallback(
		( { row } ) => (
			<EmailAndPhone
				email={ row.original.email }
				phone={ row.original.phone }
			/> ),
		[]
	);

	const onView = useCallback( ( { id } ) => {
		history.push( `/operations/contacts/${ id }` );
	}, [ history ] );

	const onDelete = useCallback( contact => {
		setToDelete( contact );
		setDeleteConfirm( <span>Are you sure you would like to delete <strong>{ contact.firstName } { contact.lastName }</strong> from the contact list?</span> );
	}, [
		setDeleteConfirm,
		setToDelete
	] );

	const tableRowActionsCell = useCallback( ( { row } ) => {
		const actions = [
			{
				action: onView,
				componentProps: { color: 'pink' },
				disable: !permissions.viewContacts.canExecute,
				icon: 'search',
				permission: permissions.viewContacts.isAvailable,
				text: 'View Contact'
			},
			...row.original.type === 'Agency' && row.original.statusID === 5
				? []
				: [
					{
						action: onDelete,
						componentProps: { negative: true },
						disable: !permissions.deleteContacts.canExecute,
						icon: 'times',
						permission: permissions.deleteContacts.isAvailable,
						text: 'Delete Contact'
					}
				]
		];

		return (
			<TableRowActions
				actions={ actions }
				row={ row.original }
			/>
		);
	}, [
		onDelete,
		onView,
		permissions
	] );

	const columns = useMemo( () => [
		{
			id: 'nameAndAddress',
			accessor: row => `${ row.lastName }${ row.firstName }${ row.address }${ row.address2 }${ row.city }${ row.state }${ row.zip }`,
			Header: 'Name and Address',
			filter: multiWordFilterFn,
			Cell: nameAndAddressCell,
			minWidth: 60
		},
		{
			id: 'organization',
			accessor: row => `${ row.organization }`,
			Header: 'Organization',
			filter: multiWordFilterFn,
			minWidth: 60
		},
		{
			id: 'contactType',
			accessor: row => `${ row.type }`,
			Header: 'Type',
			Filter: MultiSelectColumnFilter,
			filter: multiSelectFilterFn,
			minWidth: 60,
			width: 100
		},
		{
			id: 'tags',
			accessor: row => row.labels, // labels on backend
			Header: () => <><span>Tags</span><HelpModal id={ 5 } triggerSize="small" triggerStyles={ { verticalAlign: 'top' } } /></>,
			Filter: TagsColumnFilter,
			filter: tagsFilterFn,
			Cell: TagsCell,
			minWidth: 60
		},
		{
			accessor: 'title',
			Header: 'Title',
			filter: multiWordFilterFn,
			minWidth: 60
		},
		{
			id: 'emailAndPhone',
			accessor: row => `${ row.email }${ maskValue( 'phone', row.phone ) }`,
			Header: 'Email and Phone',
			filter: multiWordFilterFn,
			Cell: emailAndPhoneCell,
			minWidth: 60,
			width: 200
		}
	], [
		TagsCell,
		emailAndPhoneCell,
		nameAndAddressCell
	] );

	const actionColumn = useMemo( () => ( {
		id: 'id_actions',
		Cell: tableRowActionsCell,
		disableFilters: true,
		disableResizing: true,
		width: 100,
		minWidth: 100,
		maxWidth: 100
	} ), [ tableRowActionsCell ] );

	const renderColumns = useMemo(
		() => permissions.viewContacts.isAvailable || permissions.deleteContacts.isAvailable
			? [
				...columns,
				actionColumn
			]
			: columns,
		[
			actionColumn,
			columns,
			permissions
		]
	);

	const addContact = useCallback( () => {
		history.push( '/operations/contacts/0' );
	}, [ history ] );

	const exportAll = useCallback( async () => {
		setScreenLoader( 'Exporting contacts, Please wait...' );

		try {
			const guid = await exportContactList( null );

			await downloadAsset( guid );
		} catch ( error ) {
			setApiErrorAlert( error );
		}

		setScreenLoader( '' );
	}, [
		setApiErrorAlert,
		setScreenLoader
	] );

	const exportFiltered = useCallback( async () => {
		setScreenLoader( 'Exporting contacts, Please wait...' );

		try {
			const ids = tableState.rows.map( row => row.original.id );

			const guid = await exportContactList( ids );

			await downloadAsset( guid );
		} catch ( error ) {
			setApiErrorAlert( error );
		}

		setScreenLoader( '' );
	}, [
		setApiErrorAlert,
		setScreenLoader,
		tableState.rows
	] );

	const exportSelected = useCallback( async () => {
		setScreenLoader( 'Exporting contacts, Please wait...' );

		try {
			const ids = tableState.selectedIds;

			const guid = await exportContactList( ids );

			await downloadAsset( guid );
		} catch ( error ) {
			setApiErrorAlert( error );
		}

		setScreenLoader( '' );
	}, [
		setApiErrorAlert,
		setScreenLoader,
		tableState.selectedIds
	] );

	const actions = useMemo( () => [
		{
			action: {
				'All Rows': exportAll,
				...tableState.filters.length && tableState.rows.length
					? { 'Filtered Rows': exportFiltered }
					: {},
				...tableState.selectedIds.length
					? { 'Selected Rows': exportSelected }
					: {}
			},
			disable: !permissions.listContacts.canExecute,
			dropdown: true,
			helpId: 4,
			icon: 'cog',
			permission: permissions.listContacts.isAvailable,
			text: 'Export CSV'
		},
		{
			action: addContact,
			disable: !permissions.addContacts.canExecute,
			icon: 'add',
			permission: permissions.addContacts.isAvailable,
			text: 'Add Contact'
		}
	], [
		addContact,
		exportAll,
		exportFiltered,
		exportSelected,
		permissions,
		tableState.filters,
		tableState.rows,
		tableState.selectedIds
	] );

	if ( isLoading ) {
		return null;
	}

	return (
		<div className="page">
			{ log( 'contactList' ) }
			<PageHeader
				actions={ actions }
				title={
					<>
						<span>Contact List</span>
						<HelpModal
							id={ 3 }
							triggerStyles={ { verticalAlign: 'top' } }
						/>
					</>
				}
			/>
			<Table
				columns={ renderColumns }
				data={ contactList }
				hasPermission={ permissions.listContacts.canExecute }
				selectable
				setTableState={ setTableState }
				tableState={ tableState }
			/>
			<DeleteConfirmAlert />
		</div>
	);
};

export default memo( ContactsList );
